import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
// import Clickable from "@insite/mobius/Clickable"; // Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import CarouselArrow, {
    CarouselArrowPresentationProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselArrow";
import { focusStyles } from "@store/Themes/common/mixins/common";
import { imageStyles } from "@store/Themes/common/mixins/image";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";

const imageWidth = pxToRem(70);
const imageSpacing = pxToRem(6);
const imageCount = 5;

export const productImageCarouselImageWidth = imageWidth;
/* Custom Code - End */

interface OwnProps {
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    // Custom Code
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    carouselPreviousArrow?: CarouselArrowPresentationProps;
    carouselNextArrow?: CarouselArrowPresentationProps;
    clickable?: ClickablePresentationProps;
}

/* Custom Code - Start */
const getImageCss = (borderColor: string) => css`
    ${x => x.theme.lazyImage?.defaultProps?.css}
    border: ${pxToRem(1)} solid ${getColor(borderColor)};
    cursor: pointer;
    z-index: -1; /* Fix outline appearance on focus */
`;
/* Custom Code - End */

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainWrapper: {
        css: css`
            ${spacingStyles("1em", "1em")}
            position: relative;
            justify-content: center;
            height: 100%;
            width: 100%;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            max-width: calc(${imageWidth} * ${imageCount} + ((${imageSpacing} * 2) * ${imageCount}));
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0 ${imageSpacing};
            position: relative;
        `,
    },
    image: {
        css: getImageCss("transparent"),
    },
    selectedImage: {
        css: getImageCss("text.main"),
    },
    carouselPreviousArrow: {
        direction: "left",
        extendedStyles: {
            carouselButton: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            padding-right: ${imageSpacing};
                        `,
                    },
                },
            },
        },
    },
    carouselNextArrow: {
        direction: "right",
        extendedStyles: {
            carouselButton: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            padding-left: ${imageSpacing};
                        `,
                    },
                },
            },
        },
    },
    clickable: {
        css: css`
            ${focusStyles(true)}
            ${x => imageStyles(x.theme.design.product.image.small.ratio)}
            width: ${imageWidth};
        `,
    },
};

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;
    const nbImagesMinForArrow = 2; // Custom Code

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);

    useEffect(() => {
        if (!embla || !product.images) {
            return;
        }

        embla.reInit({
            align: "start", // Custom Code
            slidesToScroll: 1, // Custom Code
            loop: true, // Custom Code
        });
        setCanScroll();
    }, [embla, product]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images?.length === 0) {
        return null;
    }

    /* Custom Code - Change GridItem for StyledWrapper */
    return (
        <StyledWrapper {...styles.mainWrapper} key={product.id}>
            <StyledWrapper {...styles.innerWrapper}>
                {/* Custom Code - Start */}
                {product.images.length > nbImagesMinForArrow && (
                    <CarouselArrow
                        {...styles.carouselPreviousArrow}
                        carouselButton={{
                            button: {
                                disabled: !canScrollPrev,
                                onClick: () => embla && embla.scrollPrev(),
                            },
                            label: translate("Previous slide"),
                        }}
                    />
                )}
                {/* Custom Code - End */}

                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                        {product.images.map((productImage, index) => (
                            <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                <StyledWrapper {...styles.carouselSlideInner} data-test-selector="carousel_slide">
                                    <Clickable
                                        onClick={() => imageClickHandler(index)}
                                        {...styles.clickable} // Custom Code
                                    >
                                        <LazyImage
                                            {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                            src={getProductImageThumbPath(productImage)}
                                            altText={productImage.imageAltText}
                                        />
                                    </Clickable>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>

                {/* Custom Code - Start */}
                {product.images.length > nbImagesMinForArrow && (
                    <CarouselArrow
                        {...styles.carouselNextArrow}
                        carouselButton={{
                            button: {
                                disabled: !canScrollNext,
                                onClick: () => embla && embla.scrollNext(),
                            },
                            label: translate("Next slide"),
                        }}
                    />
                )}
                {/* Custom Code - End */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
